import { useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib'

type KinescopeVideoPlayerProps = {
  kinescopeId: string
  title: string
  poster?: string | null
  className?: string
  initialProgressSeconds?: number | null
  onPlaybackError?: () => void
  onPlaybackCheckpoint?: (payload: {
    positionSeconds: number
    durationSeconds: number | null
    completed: boolean
  }) => void
}

const SDK_URL = 'https://player.kinescope.io/latest/iframe.player.js'
// Тот же шаг, что у прежнего плеера: раз в 10 секунд просмотра. Иначе позиция
// у людей, пересевших на кинескоп, начала бы сохраняться иначе, чем у остальных.
const CHECKPOINT_STEP_SECONDS = 10

// События перечислены поимённо, а не через Record<string, string>: с индексной
// сигнатурой каждое обращение даёт string | undefined, и опечатка в названии
// события прошла бы молча — плеер просто перестал бы сохранять позицию.
type KinescopeEvents = {
  Ready: string
  DurationChange: string
  TimeUpdate: string
  Pause: string
  Ended: string
  Error: string
}

type KinescopePlayer = {
  on: (event: string, handler: (payload?: unknown) => void) => void
  seekTo: (seconds: number) => Promise<void> | void
  destroy: () => Promise<void> | void
  Events: KinescopeEvents
}

type KinescopeGlobal = {
  IframePlayer: {
    create: (
      elementId: string,
      options: Record<string, unknown>
    ) => Promise<KinescopePlayer>
  }
}

declare global {
  interface Window {
    Kinescope?: KinescopeGlobal
  }
}

let sdkPromise: Promise<KinescopeGlobal> | null = null

/**
 * Загрузка SDK ровно один раз на вкладку. Каждый экземпляр плеера, тянущий
 * скрипт заново, — это гонка за одну и ту же глобальную переменную.
 */
const loadKinescopeSdk = (): Promise<KinescopeGlobal> => {
  if (window.Kinescope?.IframePlayer) {
    return Promise.resolve(window.Kinescope)
  }
  if (sdkPromise) {
    return sdkPromise
  }

  sdkPromise = new Promise<KinescopeGlobal>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_URL}"]`)
    const script = existing ?? document.createElement('script')
    const onLoad = () => {
      if (window.Kinescope?.IframePlayer) {
        resolve(window.Kinescope)
      } else {
        reject(new Error('Kinescope SDK загрузился без IframePlayer'))
      }
    }

    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error('не удалось загрузить Kinescope SDK')),
      { once: true }
    )

    if (!existing) {
      script.src = SDK_URL
      script.async = true
      document.head.appendChild(script)
    }
  }).catch((error: unknown) => {
    // Иначе одна неудачная загрузка запомнилась бы навсегда и следующий
    // просмотр уже не имел бы шанса.
    sdkPromise = null
    throw error
  })

  return sdkPromise
}

let playerSeq = 0

export function KinescopeVideoPlayer({
  kinescopeId,
  title,
  poster,
  className,
  initialProgressSeconds = null,
  onPlaybackError,
  onPlaybackCheckpoint,
}: KinescopeVideoPlayerProps) {
  const [containerId] = useState(() => `kinescope-player-${(playerSeq += 1)}`)
  const [failed, setFailed] = useState(false)
  const lastCheckpointSecondRef = useRef(-1)
  const durationRef = useRef<number | null>(null)
  // Колбэки держим в ref: пересоздавать плеер из-за новой ссылки на функцию
  // означало бы перезагружать ролик у человека посреди просмотра.
  const checkpointRef = useRef(onPlaybackCheckpoint)
  const errorRef = useRef(onPlaybackError)
  checkpointRef.current = onPlaybackCheckpoint
  errorRef.current = onPlaybackError

  useEffect(() => {
    let disposed = false
    let player: KinescopePlayer | null = null
    lastCheckpointSecondRef.current = -1
    durationRef.current = null
    setFailed(false)

    const emit = (positionSeconds: number, completed: boolean) => {
      if (!Number.isFinite(positionSeconds)) return
      checkpointRef.current?.({
        positionSeconds: Math.max(0, Math.floor(positionSeconds)),
        durationSeconds: durationRef.current,
        completed,
      })
    }

    void loadKinescopeSdk()
      .then(async (sdk) => {
        if (disposed) return

        player = await sdk.IframePlayer.create(containerId, {
          url: `https://kinescope.io/${kinescopeId}`,
          size: { width: '100%', height: '100%' },
          behavior: { autoPlay: false },
          ui: { language: 'ru' },
        })

        if (disposed) {
          void player.destroy()
          return
        }

        const events = player.Events

        player.on(events.Ready, (payload?: unknown) => {
          const duration = (payload as { data?: { duration?: number } } | undefined)?.data?.duration
          if (typeof duration === 'number' && Number.isFinite(duration)) {
            durationRef.current = Math.max(0, Math.floor(duration))
          }
          if (initialProgressSeconds && initialProgressSeconds > 0) {
            void player?.seekTo(initialProgressSeconds)
          }
        })

        player.on(events.DurationChange, (payload?: unknown) => {
          const duration = (payload as { data?: { duration?: number } } | undefined)?.data?.duration
          if (typeof duration === 'number' && Number.isFinite(duration)) {
            durationRef.current = Math.max(0, Math.floor(duration))
          }
        })

        player.on(events.TimeUpdate, (payload?: unknown) => {
          const time = (payload as { data?: { currentTime?: number } } | undefined)?.data?.currentTime
          if (typeof time !== 'number' || !Number.isFinite(time)) return
          const second = Math.floor(time)
          const shouldEmit =
            lastCheckpointSecondRef.current < 0 ||
            second - lastCheckpointSecondRef.current >= CHECKPOINT_STEP_SECONDS
          if (!shouldEmit) return
          lastCheckpointSecondRef.current = second
          emit(time, false)
        })

        player.on(events.Pause, (payload?: unknown) => {
          const time = (payload as { data?: { currentTime?: number } } | undefined)?.data?.currentTime
          if (typeof time === 'number') emit(time, false)
        })

        player.on(events.Ended, () => {
          emit(durationRef.current ?? 0, true)
        })

        player.on(events.Error, () => {
          errorRef.current?.()
        })
      })
      .catch(() => {
        if (disposed) return
        setFailed(true)
        errorRef.current?.()
      })

    return () => {
      disposed = true
      void player?.destroy()
    }
  }, [containerId, kinescopeId, initialProgressSeconds])

  if (failed) {
    return (
      <div className={cn('absolute inset-0 flex items-center justify-center bg-[#0A0A0A] px-4', className)}>
        <p className="text-center text-sm text-white/80">
          Плеер не загрузился. Обновите страницу — если не помогло, напишите в поддержку.
        </p>
      </div>
    )
  }

  return (
    <div
      id={containerId}
      className={cn('absolute inset-0 h-full w-full', className)}
      aria-label={title}
      style={poster ? { backgroundImage: `url(${poster})`, backgroundSize: 'cover' } : undefined}
    />
  )
}

KinescopeVideoPlayer.displayName = 'KinescopeVideoPlayer'
